import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, SellingPlanUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { yieldToMainThread } from '@theme/utilities';

class SubscriptionAllPlansComponent extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.#onPlanChange);
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);

    const initialChecked = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked')
    );
    if (initialChecked) this.#syncForm(initialChecked);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#onPlanChange);
    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  #onPlanChange = (/** @type {Event} */ event) => {
    const radio = /** @type {HTMLInputElement} */ (event.target);
    if (!(radio instanceof HTMLInputElement) || radio.type !== 'radio') return;

    const newVariantId = radio.dataset.variantId;
    const currentVariantId = this.dataset.variantId;

    this.#syncForm(radio);
    this.#notifySellingPlanUpdate(radio);

    if (newVariantId && newVariantId !== currentVariantId) {
      this.#fetchVariant(newVariantId);
    }
  };

  /** @param {HTMLInputElement} radio */
  #notifySellingPlanUpdate(radio) {
    this.dispatchEvent(
      new SellingPlanUpdateEvent({
        price: radio.dataset.price,
        comparePrice: radio.dataset.comparePrice,
        variantId: radio.dataset.variantId,
        variantTitle: radio.dataset.variantTitle,
        sellingPlanId: radio.dataset.sellingPlanId,
        productId: this.dataset.productId,
      })
    );
  }

  #onVariantUpdate = (/** @type {any} */ event) => {
    if (event.target === this) return;

    const newVariantId = event.detail?.resource?.id?.toString();
    if (!newVariantId) return;

    this.dataset.variantId = newVariantId;

    const matchingRadio = /** @type {HTMLInputElement | null} */ (
      this.querySelector(`input[data-variant-id="${newVariantId}"]`)
    );
    if (matchingRadio) {
      matchingRadio.checked = true;
      this.#syncForm(matchingRadio);
    }
  };

  /** @returns {HTMLFormElement | null} */
  #getForm() {
    const section = this.closest('.shopify-section');
    return /** @type {HTMLFormElement | null} */ (section?.querySelector('form[data-type="add-to-cart-form"]'));
  }

  /** @param {HTMLInputElement} radio */
  #syncForm(radio) {
    const form = this.#getForm();
    if (!form) return;

    const variantInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="id"]'));
    if (variantInput) variantInput.value = radio.dataset.variantId ?? '';

    let sellingPlanInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="selling_plan"]'));
    if (!sellingPlanInput) {
      sellingPlanInput = document.createElement('input');
      sellingPlanInput.type = 'hidden';
      sellingPlanInput.name = 'selling_plan';
      form.appendChild(sellingPlanInput);
    }
    sellingPlanInput.value = radio.dataset.sellingPlanId ?? '';
  }

  /** @param {string} variantId */
  #fetchVariant(variantId) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const productUrl = this.dataset.productUrl;
    if (!productUrl) return;

    fetch(`${productUrl}?variant=${variantId}`, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        // Update URL to reflect new variant
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        yieldToMainThread().then(() => {
          history.replaceState({}, '', url.toString());
        });

        // Track the new variant
        this.dataset.variantId = variantId;

        // Morph the variant-picker if it's on the page so its selected state stays in sync
        const variantPicker = document.querySelector('variant-picker');
        const newVariantPicker = html.querySelector('variant-picker');
        if (variantPicker && newVariantPicker) {
          morph(variantPicker, newVariantPicker, MORPH_OPTIONS);
        }

        // Dispatch VariantUpdateEvent so price, images, inventory, and buy-button all update
        const textContent = html.querySelector('variant-picker script[type="application/json"]')?.textContent;
        if (textContent) {
          this.dispatchEvent(
            new VariantUpdateEvent(JSON.parse(textContent), variantId, {
              html,
              productId: this.dataset.productId ?? '',
            })
          );
        }

        // The server-rendered price above reflects the variant's regular price, not the
        // selected selling plan's price, so re-apply the selling plan price on top of it.
        const checkedRadio = /** @type {HTMLInputElement | null} */ (
          this.querySelector('input[type="radio"]:checked')
        );
        if (checkedRadio) this.#notifySellingPlanUpdate(checkedRadio);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') console.error(error);
      });
  }
}

if (!customElements.get('subscription-all-plans')) {
  customElements.define('subscription-all-plans', SubscriptionAllPlansComponent);
}
